import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { apiRequest } from "@/lib/queryClient";
import { cashScopeKey } from "./use-branch-cash";
import { invalidateSupplierReceiptCashCaches } from "@/lib/supplier-payment-cache";
import type { ReceiptAllocation, SupplierPaymentReceipt } from "@/lib/cash-supplier-payment";

export type SupplierPaymentOptions = {
  suppliers: { id: string; name: string; nameAr: string | null }[];
  balance: { outstanding: string; creditOnAccount: string; netOwed: string } | null;
  receipts: SupplierPaymentReceipt[];
  canAllocate: boolean;
};
export type CashSupplierPaymentPayload = { cashBoxId: string; forBranchId?: string; supplierId: string; mode: "receipts" | "on_account"; amount: string; notes: string; idempotencyKey: string; photoDataUrl: string; allocations: ReceiptAllocation[] };
export function useCashSupplierPaymentOptions(cashBoxId?: string, supplierId?: string, enabled = true, forBranchId?: string) {
  const { currentWorkspace, user, roles } = useAuth();
  return useQuery<SupplierPaymentOptions>({
    queryKey: ["cash-supplier-payment-options", ...cashScopeKey(currentWorkspace?.id, user, roles.find(r => r.id === user?.roleId)?.permissions.map(p => p.id)), cashBoxId || "", supplierId || "", forBranchId || ""],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (cashBoxId) params.set("cashBoxId", cashBoxId);
      if (supplierId) params.set("supplierId", supplierId);
      if (forBranchId) params.set("forBranchId", forBranchId);
      return (await apiRequest("GET", `/api/cash/supplier-payments/options?${params}`)).json();
    },
    enabled: enabled && !!currentWorkspace?.id && !!user,
    staleTime: 0, refetchOnMount: "always",
  });
}
export function useSaveCashSupplierPayment() {
  const cache = useQueryClient();
  const { currentWorkspace } = useAuth();
  return useMutation<{ paymentIds: string[]; movementIds: string[] }, Error, CashSupplierPaymentPayload>({
    mutationFn: async body => (await apiRequest("POST", "/api/cash/supplier-payments", body)).json(),
    onSuccess: () => { void invalidateSupplierReceiptCashCaches(cache, currentWorkspace?.id || ""); },
  });
}
export function useAllocateSupplierPayment() {
  const cache = useQueryClient();
  const { currentWorkspace } = useAuth();
  return useMutation<{ ok: true }, Error, { paymentId: string; idempotencyKey: string; allocations: ReceiptAllocation[] }>({
    mutationFn: async ({ paymentId, ...body }) => (await apiRequest("POST", `/api/supplier-payments/${encodeURIComponent(paymentId)}/allocate`, body)).json(),
    onSuccess: () => { void invalidateSupplierReceiptCashCaches(cache, currentWorkspace?.id || ""); },
  });
}